import React, { useMemo } from "react"

import Carousel from "decentraland-gatsby/dist/components/Carousel/Carousel"
import ImgFixed from "decentraland-gatsby/dist/components/Image/ImgFixed"
import Paragraph from "decentraland-gatsby/dist/components/Text/Paragraph"
import SubTitle from "decentraland-gatsby/dist/components/Text/SubTitle"
import useAsyncMemo from "decentraland-gatsby/dist/hooks/useAsyncMemo"
import chunk from "decentraland-gatsby/dist/utils/array/chunk"
import TokenList from "decentraland-gatsby/dist/utils/dom/TokenList"
import once from "decentraland-gatsby/dist/utils/function/once"
import { Button } from "decentraland-ui/dist/components/Button/Button"
import { useMobileMediaQuery } from "decentraland-ui/dist/components/Media/Media"
import fetch from "isomorphic-fetch"

import jumpIn from "../../images/jump-in.svg"
import LoadingFeed from "./LoadingFeed"
import { FeedProps } from "./types"
import { ellipsis, useFeedColumnName } from "./utils"

import "./EventsFeed.css"

export type EventsFeedProps = FeedProps

const DEFAULT_COLUMNS = 4

const fetchEvents = once(async () => {
  const response = await fetch(
    `https://events.decentraland.org/api/events?list=upcoming&limit=8`
  )
  const result = await response.json()
  return result.data
})

export default React.memo(function EventsFeed(props: EventsFeedProps) {
  const columns = props.columns || DEFAULT_COLUMNS
  const columnClassName = useFeedColumnName(columns, DEFAULT_COLUMNS)
  const isMobile = useMobileMediaQuery()
  const [events, state] = useAsyncMemo(fetchEvents, [])

  const eventsGroup = useMemo<any[][]>(() => {
    if (!events || events.length === 0) {
      return []
    }

    if (isMobile) {
      return Array.from(chunk(events, 1))
    }

    return Array.from(chunk(events, columns || DEFAULT_COLUMNS))
  }, [events, isMobile, columns])

  if (state.loading) {
    return (
      <LoadingFeed
        className="EventsFeed"
        columns={columns}
        visible={props.visible}
      />
    )
  }

  return (
    <Carousel className="Feed EventsFeed">
      {eventsGroup.map((events, i) => {
        return (
          <div key={`group::${i}`} className="Feed__List">
            {events.map((event) => {
              const href = event.live
                ? event.url
                : `https://events.decentraland.org/event/?id=${event.id}`
              return (
                <a
                  key={event.id}
                  className={TokenList.join(["Feed__Item", columnClassName])}
                  href={href}
                  target="_blank"
                >
                  <div className="Feed__Item__Background" />
                  <ImgFixed
                    dimension="square"
                    src={event.image}
                    background="black"
                  />
                  {event.total_attendees > 0 && (
                    <div className="Feed__Item__Concurrence">
                      +{event.total_attendees}
                    </div>
                  )}
                  <div className="Feed__Item__Detail animated">
                    <div className="Feed__Item__TitleContainer">
                      <SubTitle>
                        {event.total_attendees > 0 && (
                          <div className="Feed__Item__Concurrence">
                            +{event.total_attendees}
                          </div>
                        )}
                        {ellipsis(event.name, 65)}
                      </SubTitle>
                      <Paragraph>{ellipsis(event.description, 85)}</Paragraph>
                    </div>
                    <div className="Feed__Item__ActionContainer">
                      <Button primary size="small">
                        JUMP IN{" "}
                        <img
                          src={jumpIn}
                          width="16"
                          height="16"
                          className="JumpInIcon"
                        />
                      </Button>
                    </div>
                  </div>
                </a>
              )
            })}
          </div>
        )
      })}
    </Carousel>
  )
})
